import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Close from '@mui/icons-material/Close'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import { Box, IconButton, Tooltip } from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import {
  DIAGNOSIS_STATUS_OPTIONS,
  DIAGNOSIS_SUB_SECTION_IDS,
  MOMENT_FORMATS,
} from 'src/consumer/constants'
import { getPastDateFromTimeString } from 'src/mentor/helpers/utilityFunctions'
import { commonStylesPrescriptionSelect } from '../../../PrescriptionMakingPageComponents'
import {
  DiagnosisCreatableSelect,
  DiagnosisDurationSelect,
} from '../../../PrescriptionMakingPageStyles'
import SegmentedPillSelector from '../../common/SegmentedPillSelector'
import {
  getRenderDiagnosisSubSectionsSet,
  INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST,
  loadOptionsDiagnosisDuration,
} from '../util'
import DayjsDatePicker from './DayjsDatePicker'

function normalize(value) {
  return value == null ? '' : String(value).trim().toLowerCase()
}

/**
 * Lean diagnosis entry (mirrors the Complaints card): a persistent search box that keeps
 * appending structured rows. Each selected diagnosis renders as one compact line — name +
 * (Since date / For Duration, per the section's configured sub-sections) + optional Status
 * pills (Ruled out / Suspected / Confirmed) + delete. No table header, no empty placeholder
 * rows, no add button.
 */
export default function DiagnosisTabularSubSectionsInput({
  diagnosisList,
  subSections,
  onChangeDiagnosis,
  loadOptionsDiagnosis,
  clinicId,
  clinicBrandId,
  recommendedDiagnosisIds,
  prescriptionViewSettings,
  menuPortalTarget,
}) {
  const subSet = getRenderDiagnosisSubSectionsSet(subSections)
  const showSince = subSet.has(DIAGNOSIS_SUB_SECTION_IDS.diagnosis_since_date.id)
  const showDuration = subSet.has(DIAGNOSIS_SUB_SECTION_IDS.diagnosis_for_duration.id)

  // Ignore any empty placeholder rows left over from the old table UX — rows are added via search.
  const rows = (Array.isArray(diagnosisList) ? diagnosisList : []).filter(
    (d) => d?.value || d?.label,
  )
  const [searchKey, setSearchKey] = useState(0)

  const portal = menuPortalTarget || (typeof document !== 'undefined' ? document.body : null)
  const selectMenuStyles = {
    ...commonStylesPrescriptionSelect,
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    menu: (provided, state) => ({
      ...provided,
      ...commonStylesPrescriptionSelect.menu(provided, state),
      zIndex: 9999,
      position: 'absolute',
    }),
  }

  function updateRow(idx, patch) {
    onChangeDiagnosis(rows.map((diagnosis, index) => (index === idx ? { ...diagnosis, ...patch } : diagnosis)))
  }

  function deleteRow(idx) {
    onChangeDiagnosis(rows.filter((_, index) => index !== idx))
  }

  function addDiagnosis(option) {
    if (!option) {
      return
    }
    const key = normalize(option?.value ?? option?.label)
    const alreadyPresent =
      key && rows.some((diagnosis) => normalize(diagnosis?.value ?? diagnosis?.label) === key)
    if (!alreadyPresent) {
      onChangeDiagnosis([...rows, option]) // parent capitalises the label
    }
    setSearchKey((k) => k + 1)
  }

  // Selecting a duration also back-fills the "since" date (mirrors the previous behaviour).
  function onDurationChange(idx, val) {
    if (!val) {
      updateRow(idx, { duration: '' })
      return
    }
    const pastDate = getPastDateFromTimeString(val)
    updateRow(idx, {
      duration: val,
      date: dayjs(pastDate).format(MOMENT_FORMATS.formatThatServerSends),
    })
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.length > 0 && (
        <Box
          sx={{
            border: '1px solid rgba(67, 56, 202, 0.12)',
            borderRadius: '10px',
            overflow: 'hidden',
            background: '#fff',
          }}
        >
          {rows.map((diagnosis, idx) => (
            <DiagnosisRow
              key={idx}
              diagnosis={diagnosis}
              isLast={idx === rows.length - 1}
              showSince={showSince}
              showDuration={showDuration}
              portal={portal}
              menuPortalTarget={menuPortalTarget}
              selectMenuStyles={selectMenuStyles}
              onSince={(val) => updateRow(idx, { date: val })}
              onDuration={(val) => onDurationChange(idx, val)}
              onStatus={(val) => updateRow(idx, { status: val })}
              onDelete={() => deleteRow(idx)}
            />
          ))}
        </Box>
      )}

      <DiagnosisCreatableSelect
        key={searchKey}
        id="diagnosisAddSelection"
        classNamePrefix="diagnosis_add_selection"
        placeholder="Start typing Diagnosis…"
        createOptionPosition="first"
        openMenuOnFocus
        isSearchable
        isClearable={false}
        defaultOptions
        loadOptions={loadOptionsDiagnosis}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={null}
        controlShouldRenderValue={false}
        onChange={(val) => addDiagnosis(val)}
        onCreateOption={(input) =>
          addDiagnosis({ apiParam: 'diagnosis', value: input, label: input, __isNew__: true })
        }
        debounceTimeout={200}
        additional={{
          page: 1,
          clinicId,
          clinicBrandId,
          recommendedDiagnosisIds,
          prescriptionViewSettings,
        }}
        styles={selectMenuStyles}
        menuPortalTarget={portal}
        menuPosition={menuPortalTarget ? 'fixed' : undefined}
        components={{ ClearIndicator: () => null }}
      />
    </Box>
  )
}

function DiagnosisRow({
  diagnosis,
  isLast,
  showSince,
  showDuration,
  portal,
  menuPortalTarget,
  selectMenuStyles,
  onSince,
  onDuration,
  onStatus,
  onDelete,
}) {
  const name = diagnosis?.label || diagnosis?.title || diagnosis?.value || ''
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 1.25,
        py: 1,
        borderBottom: isLast ? 'none' : '1px solid rgba(67, 56, 202, 0.08)',
        transition: 'background 0.15s ease',
        '&:hover': { background: 'rgba(67, 56, 202, 0.02)' },
      }}
    >
      <Box
        sx={{
          flex: '1 1 140px',
          minWidth: 0,
          fontSize: 14,
          fontWeight: 500,
          color: '#1e293b',
          wordBreak: 'break-word',
        }}
      >
        {name}
      </Box>

      {/* Controls grouped so they wrap together below the name on narrow screens. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
        {showSince && (
        <Box sx={{ flex: '0 0 auto', position: 'relative', display: 'flex', alignItems: 'center' }}>
          <DayjsDatePicker
            buttonProps={{
              startIcon: <CalendarMonth sx={{ fontSize: 16 }} />,
              sx: {
                borderRadius: '6px',
                color: diagnosis?.date ? '#1e293b' : '#9ca3af',
                border: '0.5px solid rgba(67, 56, 202, 0.2)',
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                minWidth: 120,
                px: 1.25,
                py: 0.5,
                justifyContent: 'flex-start',
              },
            }}
            placeholder="Since"
            date={diagnosis?.date}
            onChange={(val) => onSince(dayjs(val).format(MOMENT_FORMATS.formatThatServerSends))}
          />
          {diagnosis?.date && (
            <Close
              sx={{ position: 'absolute', right: 6, cursor: 'pointer', color: '#CCC', fontSize: 14 }}
              onClick={() => onSince(null)}
            />
          )}
        </Box>
      )}

      {showDuration && (
        <Box sx={{ flex: '0 0 auto', minWidth: 118, maxWidth: 158 }}>
          <DiagnosisDurationSelect
            id="diagnosis_for_duration"
            classNamePrefix="diagnosis_for_duration_selection"
            placeholder="Duration"
            isSearchable
            isClearable
            defaultOptions={false}
            loadOptions={loadOptionsDiagnosisDuration}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.title}
            isOptionDisabled={(option) =>
              INITIAL_DIAGNOSIS_OPTION_ARRAY_LIST?.map((o) => o?.id).includes(option?.id)
            }
            value={diagnosis?.duration ? { id: diagnosis?.duration, title: diagnosis?.duration } : null}
            onChange={(val) => onDuration(val?.id || '')}
            additional={{}}
            styles={selectMenuStyles}
            menuPortalTarget={portal}
            menuPosition={menuPortalTarget ? 'fixed' : undefined}
            components={{ ClearIndicator: () => null }}
          />
        </Box>
      )}

        <SegmentedPillSelector
          options={DIAGNOSIS_STATUS_OPTIONS}
          value={diagnosis?.status}
          onChange={(val) => onStatus(val)}
          ariaLabel="Diagnosis status"
        />

        <Tooltip title="Remove diagnosis" arrow>
          <IconButton onClick={onDelete} size="small" sx={{ color: '#ef4444', flexShrink: 0 }}>
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}
